import { useCallback, useRef, useState } from 'react'
import AddIcon from 'assets/svgr-icons/Add.svg'
import UploadIcon from 'assets/svgr-icons/Upload.svg'
import MyFilesIcon from 'assets/svgr-icons/My_Files.svg'
import ActionsFlyout from 'common-components/ActionsFlyout'
import MediaUploadButton from 'components/MediaUploadButton'
import { useDropdownState } from 'hooks/useDropdownState'
import { useScrollableElement } from 'containers/SubSidebar/AIVideoContent/context'
import { translate } from 'utils/translations'
import { T } from 'consts/translationKeys'
import * as Styled from './styled'
import { IMyFilesMediaItem } from 'types/media'
import { openMediaLib } from 'utils/media'

interface IFramePanelProps {
  label: string
  subLabel?: string
  onMediaLibFileSelect: (files: IMyFilesMediaItem[]) => void
  onUpload: (files: File[] | FileList) => Promise<void>
  disabled?: boolean
  testId?: string
}

const EmptyFrame: React.FC<IFramePanelProps> = ({
  label,
  subLabel,
  onMediaLibFileSelect,
  onUpload,
  disabled,
  testId
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null)
  const {
    isOpen: isFlyoutOpen,
    closeDropdown: closeFlyout,
    toggleDropdown: toggleFlyout
  } = useDropdownState()

  const _openMediaLib = useCallback(() => {
    openMediaLib({
      onChoose: onMediaLibFileSelect
    })
  }, [onMediaLibFileSelect])

  const { scrollableElementRef } = useScrollableElement()
  const uploadTriggerRef = useRef<{ triggerUpload: () => void } | null>(null)

  const handleUploadClick = useCallback(() => {
    uploadTriggerRef.current?.triggerUpload()
    closeFlyout()
  }, [closeFlyout])

  const handlePanelClick = useCallback(() => {
    if (disabled) return
    toggleFlyout()
  }, [disabled, toggleFlyout])

  const actions = [
    {
      key: 'upload',
      title: translate(T.UPLOAD_LABEL_SIDEBAR),
      icon: <UploadIcon />,
      onClick: handleUploadClick,
      testId: 'upload-button'
    },
    {
      key: 'my_files',
      title: translate(T.MY_FILES_LABEL),
      icon: <MyFilesIcon />,
      onClick: _openMediaLib,
      testId: 'my-files-button'
    }
  ]

  return (
    <>
      <Styled.Panel
        ref={setAnchorEl}
        $disabled={disabled}
        onClick={handlePanelClick}
        data-test-id={testId}
      >
        <Styled.IconWrapper>
          <AddIcon />
        </Styled.IconWrapper>
        <Styled.PanelLabel type="h2" color="secondarySemiDark">
          {label}
        </Styled.PanelLabel>
        {subLabel && (
          <Styled.PanelSubLabel type="h4" color="secondary">
            {subLabel}
          </Styled.PanelSubLabel>
        )}
      </Styled.Panel>

      <MediaUploadButton
        acceptType="image"
        handleFilesUpload={onUpload}
        refTrigger={uploadTriggerRef}
      />

      <ActionsFlyout
        actions={actions}
        isOpen={isFlyoutOpen}
        anchorEl={anchorEl}
        onClose={closeFlyout}
        flyoutOptions={{
          position: 'right',
          alignmentY: 'top',
          extraOffset: 4
        }}
        isMobileWithDrawer
        scrollableElementRef={scrollableElementRef}
      />
    </>
  )
}

export default EmptyFrame
